/**
 * Client dashboard, matching docs/prototype.html.
 *
 * Built around the settled single-active-event model: a client normally runs
 * ONE event at a time, so this leads with that event rather than a portfolio
 * grid. Past and complete events are a quiet text link, not a second panel
 * competing for attention.
 *
 * Composed from the events endpoint rather than a dedicated one. The numbers
 * are the same numbers the events list uses, and a separate endpoint would be
 * a second place for them to drift.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { T } from '../lib/copyEdit.tsx';
import { useSession } from '../lib/auth.ts';

interface EventCounts {
  sponsors: number;
  approved: number;
  pendingReview: number;
  outstanding: number;
}

interface EventItem {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'complete' | 'archived';
  startsOn: string | null;
  endsOn: string | null;
  deadline: { display: string | null; daysUntil: number | null } | null;
  counts: EventCounts;
}

/**
 * Just enough of a sponsor row to list who is waiting on a decision.
 *
 * The full shape lives in Sponsors.tsx; this is the subset the panel reads, so
 * adding a field there does not oblige this file to know about it.
 */
interface PendingSponsor {
  eventSponsorId: string;
  companyName: string;
  counts: { needsReview: number };
  state: { label: string; tone: 'warn' | 'info' | 'ok' | 'neutral' };
}

/** Maps a sponsor's state tone onto the badge classes the app already uses. */
const TONE_BADGE: Record<PendingSponsor['state']['tone'], string> = {
  warn: 'b-warning',
  info: 'b-info',
  ok: 'b-success',
  neutral: 'b-neutral',
};

function describeDays(days: number): string {
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} past deadline`;
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  return `${days} days away`;
}

export default function Dashboard() {
  const { clientId = '' } = useParams();
  const { data: session } = useSession();
  /* Archiving and deleting a client are Front Porch decisions. A client
     administrator must never be offered either about their own organization;
     the server refuses them regardless, and offering a control that 403s is
     worse than offering none. */
  const isPlatformAdmin =
    (session?.user as { role?: string } | undefined)?.role === 'fps_super_admin';
  const [events, setEvents] = useState<EventItem[] | null>(null);
  /**
   * Who is actually waiting on a decision, for the panel below.
   *
   * READ FROM THE ACTIVE EVENT'S SPONSORS ENDPOINT rather than a new
   * dashboard-specific one. The rows already carry the derived state this needs
   * and it is computed server-side on every read, so borrowing it means the
   * dashboard and the event's own sponsor table can never disagree about who
   * needs reviewing. A second endpoint would be a second definition.
   *
   * Scoped to the ACTIVE event, which is this page's whole premise: a client
   * normally runs one event at a time, and the panel above already leads with
   * it. Null while loading, and an empty array once loaded with nobody waiting.
   */
  const [pending, setPending] = useState<PendingSponsor[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/clients/${clientId}/events`);
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { events: EventItem[] };
      setEvents(body.events);

      const active = body.events.find((e) => e.status === 'active');
      if (!active) {
        setPending([]);
        return;
      }

      const sponsors = await fetch(`/api/clients/${clientId}/events/${active.id}/sponsors`);
      if (!sponsors.ok) {
        /* The panel is not worth failing the page over. An empty list reads as
         * "nothing waiting", which would be a lie, so it stays null and the
         * panel says it could not load. */
        setPending(null);
        return;
      }
      const list = (await sponsors.json()) as { sponsors: PendingSponsor[] };
      setPending(list.sponsors.filter((s) => s.counts.needsReview > 0));
    } catch {
      setError('Could not load the dashboard.');
    }
  }, [clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <p className="auth-error">{error}</p>;
  if (!events) return <div className="app-loading" aria-busy="true" />;

  /* The events endpoint already returns urgency order, so the first active
   * event is the one that matters most right now. */
  const active = events.find((e) => e.status === 'active') ?? null;
  const others = events.filter((e) => e.id !== active?.id).length;

  const c = active?.counts;
  const total = c?.sponsors ?? 0;
  const percent = total > 0 ? Math.round(((c?.approved ?? 0) / total) * 100) : 0;

  return (
    <>
      <div className="phead">
        <div>
          <p className="eyebrow"><T k="common.appName" d="Sponsor Logos" /></p>
          <h1 className="title"><T k="dashboard.title" d="Dashboard" /></h1>
          <p className="lede"><T k="dashboard.lede" d="An overview of pending logos and your events." /></p>
        </div>
        <div className="pactions">
          <Link className="btn-compact btn-compact--quiet" to={`/c/${clientId}/events`}>
            <T k="dashboard.action.allEvents" d="All Events" /></Link>
          {/* Creating an event from here, which the prototype has and the build
              did not: the dashboard is where somebody lands, and the only route
              to a new event was to go to Events first and find the button
              there. The form itself is not duplicated — this opens the one on
              the Events page, so there is a single create form to keep right. */}
          <Link className="btn-compact" to={`/c/${clientId}/events?new=1`}>
            <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <T k="dashboard.action.newEvent" d="New Event" />
          </Link>

          {/* EDIT LIVES HERE NOW, and it had to go somewhere.
              It has only ever been on the card in the Clients list, and once
              the clients moved into the sidebar nobody passes that card — so
              the form became reachable only by deliberately going back to All
              Clients. A menu rather than a fourth button, because Edit is
              occasional and Archive and Delete must not sit beside the two
              things people press every day. */}
          {isPlatformAdmin && <ClientMenu clientId={clientId} />}
        </div>
      </div>

      {!active ? (
        <div className="empty">
          <p className="empty-title"><T k="dashboard.empty.title" d="No active event" /></p>
          <p className="empty-copy">
            <T
              k="dashboard.empty.copy"
              d="Set an event to Active once you are ready to collect logos, and it will lead this page."
            />
          </p>
        </div>
      ) : (
        <>
          <section className="panel">
            <div className="panel-head">
              <h3><T k="dashboard.active.title" d="Active Event" /></h3>
              {/* "Full" was doing no work, and the vivid blue competed with
                  the heading beside it. The red tick is already the app's
                  accent for "here", so hover borrows it rather than
                  introducing another colour. */}
              <Link className="tick-link" to={`/c/${clientId}/events/${active.id}`}>
                <T k="dashboard.action.openEvent" d="Open event" />
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </Link>
            </div>
            <div className="panel-body">
              {/* The name is the most obvious thing to click on this panel,
                  so it opens the event rather than only the link above it. */}
              <h2 className="hero-name">
                <Link to={`/c/${clientId}/events/${active.id}`}>{active.name}</Link>
              </h2>
              <div className="emeta">
                {active.deadline?.display && (
                  <span>
                    Logo deadline: {active.deadline.display}
                    {typeof active.deadline.daysUntil === 'number' && (
                      <>
                        {' · '}
                        <b className="hero-urgent">{describeDays(active.deadline.daysUntil)}</b>
                      </>
                    )}
                  </span>
                )}
                <span className="badge b-info">
                  <span className="d" />
                  Active
                </span>
              </div>

              <Link className="progress-link" to={`/c/${clientId}/events/${active.id}`}>
                <div className="progress-head">
                  <span>
                    {c?.approved ?? 0} of {total} sponsor{total === 1 ? '' : 's'} approved
                  </span>
                  <span className="progress-pct">{percent}%</span>
                </div>
                <div className="bar">
                  <span style={{ width: `${percent}%` }} />
                </div>
              </Link>
            </div>
          </section>

          <div className="stats">
            <div className="stat">
              <div className="label"><T k="stat.sponsors" d="Sponsors" /></div>
              <div className="n">{c?.sponsors ?? 0}</div>
              <div className="m"><T k="dashboard.stat.sponsorsSub" d="Invited" /></div>
            </div>
            <div className="stat">
              <div className="label"><T k="stat.approved" d="Approved" /></div>
              <div className="n">{c?.approved ?? 0}</div>
              <div className="m">{percent}% complete</div>
            </div>
            <div className="stat">
              <div className="label"><T k="stat.pendingReview" d="Pending review" /></div>
              <div className="n">{c?.pendingReview ?? 0}</div>
              <div className="m"><T k="stat.pendingReviewSub" d="Awaiting a decision" /></div>
            </div>
            <div className="stat">
              <div className="label"><T k="stat.outstanding" d="Outstanding" /></div>
              <div className="n">{c?.outstanding ?? 0}</div>
              <div className="m"><T k="stat.outstandingSub" d="Awaiting upload" /></div>
            </div>
          </div>

          {/* NAMES THE SPONSORS, rather than counting them. This panel used to
              say "3 sponsors have uploaded and are waiting on a decision",
              which tells you that there is work without telling you what it is
              or letting you start it. The prototype lists them, and the list is
              the whole value: the point of a dashboard is to be one click from
              the thing you came to do. */}
          <section className="panel">
            <div className="panel-head">
              <h3><T k="dashboard.review.title" d="Needs Your Review" /></h3>
              <Link className="tick-link" to={`/c/${clientId}/events/${active.id}`}>
                <T k="dashboard.action.openEvent" d="Open event" />
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </Link>
            </div>

            {pending === null ? (
              <div className="panel-body">
                <p className="fdesc" style={{ margin: 0 }}>
                  <T
                    k="dashboard.review.loadFailed"
                    d="The list of sponsors waiting on you could not be loaded. The count above is still correct."
                  />
                </p>
              </div>
            ) : pending.length === 0 ? (
              <div className="panel-body">
                <p className="fdesc" style={{ margin: 0 }}>
                  <T
                    k="dashboard.review.empty"
                    d="Nothing is waiting on you. Uploads appear here as sponsors submit."
                  />
                </p>
              </div>
            ) : (
              <ul className="dash-review">
                {pending.map((s, i) => (
                  <li key={s.eventSponsorId}>
                    <Link to={`/c/${clientId}/events/${active.id}/sponsors/${s.eventSponsorId}?tab=review`}>
                      <span className="dash-review-n">{i + 1}</span>
                      <span className="dash-review-body">
                        <b>{s.companyName}</b>
                        <span className="csub">
                          {active.name} · {s.counts.needsReview} version
                          {s.counts.needsReview === 1 ? '' : 's'} awaiting a decision
                        </span>
                      </span>
                      <span className={`badge ${TONE_BADGE[s.state.tone]}`}>
                        <span className="d" />
                        {s.state.label}
                      </span>
                      <svg className="icon chev" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      {others > 0 && (
        <p className="fdesc" style={{ marginTop: 'var(--sp3)' }}>
          Other events ·{' '}
          <Link className="clink" to={`/c/${clientId}/events`}>
            view {others} other event{others === 1 ? '' : 's'} →
          </Link>
        </p>
      )}
    </>
  );
}

/**
 * Edit, archive and delete, for the client you are standing in.
 *
 * A MENU RATHER THAN BUTTONS. Edit is occasional, and the two below it are the
 * most consequential things in the product — neither belongs in the row that
 * also holds All Events and New Event, which people press daily.
 *
 * Archive leads, and delete is last and red, because archiving is almost always
 * what somebody means. An association you have stopped working with is not one
 * that never existed.
 */
function ClientMenu({ clientId }: { clientId: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  return (
    <div className={`rowmenu${open ? ' open' : ''}`} ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="kebab"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="More for this client"
        onClick={() => setOpen((v) => !v)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      </button>

      <div className="rowmenu-list rowmenu-list--anchored" role="menu">
        <button
          type="button"
          role="menuitem"
          className="rowmenu-item"
          onClick={() => {
            setOpen(false);
            /* The one edit form, on the Clients page, opened on this client —
               rather than a second copy of fourteen fields to keep in step. */
            void navigate(`/?edit=${clientId}`);
          }}
        >
          <b><T k="client.menu.edit" d="Edit Client" /></b>
          <small><T k="client.menu.editSub" d="Brand, email sender, reminder timing" /></small>
        </button>

        <button
          type="button"
          role="menuitem"
          className="rowmenu-item"
          onClick={() => {
            setOpen(false);
            void navigate(`/c/${clientId}/events`);
          }}
        >
          <b><T k="client.menu.events" d="All Events" /></b>
          <small><T k="client.menu.eventsSub" d="Everything this client is running" /></small>
        </button>

        <div className="rowmenu-sep" />

        <button
          type="button"
          role="menuitem"
          className="rowmenu-item"
          onClick={() => {
            setOpen(false);
            void navigate(`/?archive=${clientId}`);
          }}
        >
          <b><T k="client.menu.archive" d="Archive Client" /></b>
          <small>
            <T k="client.menu.archiveSub" d="Keeps everything, closes it to them and their sponsors" />
          </small>
        </button>

        <button
          type="button"
          role="menuitem"
          className="rowmenu-item rowmenu-item--danger"
          onClick={() => {
            setOpen(false);
            void navigate(`/?delete=${clientId}`);
          }}
        >
          <b><T k="client.menu.delete" d="Delete Client" /></b>
          <small><T k="client.menu.deleteSub" d="Removes every event, sponsor and logo" /></small>
        </button>
      </div>
    </div>
  );
}
